import type { ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@web/components/ui/button";
import { Input } from "@web/components/ui/input";

// 统一右侧控件宽度 保持设置项视觉对齐
const settingControlColumnClass = "w-[12.5rem] justify-end";

export function SettingGroup({ children }: { children: ReactNode }) {
  return <section className="space-y-4">{children}</section>;
}

export function SettingSelectRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <SettingControlRow label={label}>
      <div className={createSettingControlColumnClass()}>{children}</div>
    </SettingControlRow>
  );
}

export function SettingExpandableSelectRow({
  label,
  expanded,
  children,
  toggleLabel,
  onToggleExpanded,
}: {
  label: string;
  expanded: boolean;
  children: ReactNode;
  toggleLabel: string;
  onToggleExpanded: () => void;
}) {
  return (
    <SettingControlRow label={label} gapClassName="gap-3">
      <div className={createSettingControlColumnClass("gap-2")}>
        {children}
        <Button type="button" size="icon-sm" variant="outline" aria-label={toggleLabel} onClick={onToggleExpanded}>
          {expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </Button>
      </div>
    </SettingControlRow>
  );
}

export function SettingColorRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <SettingControlRow label={label} gapClassName="gap-3">
      <div className={createSettingControlColumnClass("gap-3")}>{children}</div>
    </SettingControlRow>
  );
}

export function SettingSwitchRow({
  label,
  checked,
  disabled,
  onToggle,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <SettingControlRow label={label}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border bg-muted p-0.5 transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-50 aria-checked:bg-primary focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
        onClick={onToggle}
      >
        <span className="sr-only">{label}</span>
        <span className="size-5 rounded-full bg-background shadow-sm transition-transform aria-checked:translate-x-5" aria-checked={checked} />
      </button>
    </SettingControlRow>
  );
}

export function SettingColorSwatch({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  value: string;
  fallback: string;
  onChange: (value: string) => void;
}) {
  const displayColor = normalizeColorValue(value, fallback);

  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
      <span>{label}</span>
      <span className="relative size-7 overflow-hidden rounded-full border shadow-inner" style={{ backgroundColor: displayColor }}>
        <Input
          type="color"
          value={displayColor}
          aria-label={label}
          className="absolute -inset-2 size-11 cursor-pointer border-0 p-0 opacity-0"
          onChange={event => onChange(event.target.value)}
        />
      </span>
    </label>
  );
}

export function SettingStepper({
  label,
  value,
  onLess,
  onMore,
}: {
  label: string;
  value: string | number;
  onLess: () => void;
  onMore: () => void;
}) {
  return (
    <SettingControlRow label={label} layoutClassName="grid grid-cols-[minmax(3rem,1fr)_auto] items-center gap-5">
      <div className={createSettingControlColumnClass("gap-2")}>
        <Button size="icon-sm" variant="outline" onClick={onLess}>
          -
        </Button>
        <span className="w-16 text-center text-sm text-muted-foreground">{value}</span>
        <Button size="icon-sm" variant="outline" onClick={onMore}>
          +
        </Button>
      </div>
    </SettingControlRow>
  );
}

function SettingControlRow({
  label,
  children,
  gapClassName = "gap-4",
  layoutClassName,
}: {
  label: string;
  children: ReactNode;
  gapClassName?: string;
  layoutClassName?: string;
}) {
  const className = layoutClassName ?? `flex items-center justify-between ${gapClassName}`;

  return (
    <div className={className}>
      <p className="min-w-12 text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

function createSettingControlColumnClass(extraClassName?: string): string {
  return ["flex items-center", settingControlColumnClass, extraClassName].filter(Boolean).join(" ");
}

function normalizeColorValue(value: string, fallback: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}
